
import { useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { validateAndSanitizeCSS } from "@/utils/cssUtils";

export const useCssManager = () => {
  const [cssOverride, setCssOverride] = useState("");
  const [originalCss, setOriginalCss] = useState("");
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const { toast } = useToast();

  // Fetch CSS override from the database
  const fetchCssOverride = async () => {
    setIsLoading(true);
    setError(null);
    
    try {
      // Load CSS from database
      const { data, error } = await supabase
        .from('admin_settings')
        .select('setting_value')
        .eq('setting_key', 'css_override')
        .maybeSingle();

      if (error) throw error;
      
      // Process the data
      if (data && data.setting_value) {
        let cssValue = typeof data.setting_value === 'string' 
          ? data.setting_value 
          : (JSON.stringify(data.setting_value) === '{}' 
              ? "/* Add your custom CSS here */\n\n" 
              : String(data.setting_value));
        
        setCssOverride(cssValue);
        setOriginalCss(cssValue);
      } else {
        // Set default CSS if no data was found
        const defaultCss = "/* Add your custom CSS here */\n\n";
        setCssOverride(defaultCss);
        setOriginalCss(defaultCss);
      }
    } catch (error) {
      console.error('Error fetching CSS override:', error);
      setError("Failed to load CSS settings. Please try refreshing the page.");
      toast({
        variant: "destructive",
        title: "Error",
        description: "Failed to load CSS override settings"
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Save CSS override to the database
  const saveCssOverride = async () => {
    setIsSaving(true);
    setError(null);
    setSuccessMessage(null);
    
    try {
      // Validate and sanitize CSS
      const { css: sanitizedCss, isValid, errors } = validateAndSanitizeCSS(cssOverride);
      
      if (!isValid) {
        throw new Error(errors.join(", ") || "Invalid CSS syntax detected");
      }
      
      // Update in the database
      const { error: dbError } = await supabase
        .from('admin_settings')
        .upsert({
          setting_key: 'css_override',
          setting_value: sanitizedCss,
          description: 'Custom CSS for the application',
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'setting_key'
        });
      
      if (dbError) throw dbError;

      // Update the original CSS reference
      setOriginalCss(sanitizedCss);
      
      // Dispatch event to trigger CSS refresh
      window.dispatchEvent(new CustomEvent('custom-css-updated'));
      
      setSuccessMessage("Custom CSS has been updated and applied");
      
      toast({
        title: "Settings saved",
        description: "Custom CSS has been updated and applied to the site"
      });
      
    } catch (error) {
      console.error('Error saving CSS override:', error);
      setError(error.message || "Failed to save CSS");
      toast({
        variant: "destructive",
        title: "Error",
        description: error.message || "Failed to save CSS override settings"
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Reset CSS to last saved value
  const resetCssOverride = () => {
    setCssOverride(originalCss);
    setError(null);
    setSuccessMessage(null);
  };

  return {
    cssOverride,
    setCssOverride,
    originalCss,
    isLoading,
    isSaving,
    error,
    successMessage,
    setError,
    setSuccessMessage,
    fetchCssOverride,
    saveCssOverride,
    resetCssOverride
  };
};
